import api from './api'
import loggingService from './loggingService'

/**
 * Client service for the independent-tutoring legal agreement flow.
 *
 * Each party (coach, guardian, student) signs their own role-specific agreement before a
 * coach-client relationship can become active. Mirrors the server routes mounted at /api/tutoring.
 */

// Versioned ConsentDocument slug for each signer role (mirrors server INDEPENDENT_TUTORING_CONFIG).
export const TUTORING_SLUG_BY_ROLE = {
  coach: 'coach-tutoring-agreement',
  guardian: 'tutoring-guardian-consent',
  student: 'tutoring-student-ack'
}

// Short human label per signer role, for buttons/headings.
export const TUTORING_ROLE_LABELS = {
  coach: 'Coach agreement',
  guardian: 'Guardian consent',
  student: 'Student acknowledgment'
}

const extractErrorMessage = (error, fallback) =>
  error?.response?.data?.error || error?.response?.data?.message || error?.message || fallback

const tutoringService = {
  /**
   * Fetch the current published version of a role's agreement document.
   * @param {string} slug - one of TUTORING_SLUG_BY_ROLE values
   */
  async getDocument(slug) {
    try {
      const response = await api.get(`/tutoring/documents/${slug}`)
      return response.data?.document
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load agreement document')
      loggingService.error('Error fetching tutoring document', { slug, message })
      throw new Error(message)
    }
  },

  /**
   * Get readiness (what is still outstanding) for a relationship.
   * @param {string} relationshipId
   */
  async getReadiness(relationshipId) {
    try {
      const response = await api.get(`/tutoring/relationships/${relationshipId}/readiness`)
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to load agreement status')
      loggingService.error('Error fetching tutoring readiness', { relationshipId, message })
      throw new Error(message)
    }
  },

  /**
   * Record the authenticated user's signature for a role on a relationship.
   * @param {string} relationshipId
   * @param {{ signerRole: string, consentTypes: string[], signatureName: string }} payload
   * @returns {Promise<{ success: boolean, signatureId: string, activated: boolean, missing: Array }>}
   */
  async sign(relationshipId, { signerRole, consentTypes, signatureName }) {
    try {
      const response = await api.post(`/tutoring/relationships/${relationshipId}/sign`, {
        signerRole,
        consentTypes,
        signatureName
      })
      return response.data
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to record signature')
      loggingService.error('Error signing tutoring agreement', {
        relationshipId,
        signerRole,
        message
      })
      // Preserve the server error code (e.g. RELATIONSHIP_NOT_SIGNABLE) so the UI can react.
      const wrapped = new Error(message)
      wrapped.code = error?.response?.data?.code || null
      throw wrapped
    }
  }
}

export default tutoringService
